import type { FieldDefinition } from "@estelle/domain";

import { CREDIT_NOTE_REASONS } from "@/modules/gst/reasons";

/**
 * The fields the GST module offers to the filter builder, the column
 * chooser and the sort control — for the Invoices section specifically.
 *
 * Covers every filter FR-GST-17 names — status, a date range on
 * `invoiceDate`, buyer state and tax type — plus the identifying columns a
 * list of tax documents is read by. `CreditNotes` does not share this array
 * (see the comment on `CREDIT_NOTE_FIELDS`), so this is the module's fields
 * for `ModuleUi` purposes.
 *
 * Order matters — the column chooser takes the first six as the default set,
 * so identifying fields come first and situational ones after.
 */
export const INVOICE_FIELDS: FieldDefinition[] = [
  {
    name: "invoiceNumber",
    label: "Invoice number",
    type: "text",
    sortable: true,
  },
  { name: "invoiceDate", label: "Invoice date", type: "date", sortable: true },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "draft", label: "Draft" },
      { value: "issued", label: "Issued" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  { name: "buyerName", label: "Buyer", type: "text", sortable: true },
  {
    name: "placeOfSupplyStateCode",
    label: "Buyer state",
    type: "text",
    sortable: true,
  },
  { name: "total", label: "Total", type: "money", sortable: true },
  {
    name: "taxKind",
    label: "Tax type",
    type: "enum",
    sortable: true,
    options: [
      { value: "cgst_sgst", label: "CGST + SGST" },
      { value: "igst", label: "IGST" },
    ],
  },
  { name: "buyerGstin", label: "Buyer GSTIN", type: "text" },
];

/**
 * Fields for the Credit Notes list.
 *
 * Declared separately from `INVOICE_FIELDS` rather than merged into it: a
 * `CreditNote` row has no `invoiceNumber` or `buyerName`, and `ModuleUi`
 * carries one flat `fields` array shared by every section's shared toolbar.
 * Wiring that one array to `gstModule.fields` (so the Invoices section keeps
 * the shared filter builder FR-GST-17 asks for) means the Credit Notes
 * section cannot also drive that same toolbar from a different field set —
 * see `screens/CreditNotes.tsx` for how it still gets a column chooser and a
 * sort control built from this array, just running on state that lives in
 * the screen instead of the URL.
 *
 * Order matters here too, for the same reason as `INVOICE_FIELDS`.
 */
export const CREDIT_NOTE_FIELDS: FieldDefinition[] = [
  {
    name: "creditNoteNumber",
    label: "Credit note number",
    type: "text",
    sortable: true,
  },
  {
    name: "creditNoteDate",
    label: "Date",
    type: "date",
    sortable: true,
  },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "draft", label: "Draft" },
      { value: "issued", label: "Issued" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  {
    name: "reason",
    label: "Reason",
    type: "enum",
    sortable: true,
    options: CREDIT_NOTE_REASONS,
  },
  { name: "total", label: "Total", type: "money", sortable: true },
  {
    name: "taxKind",
    label: "Tax type",
    type: "enum",
    sortable: true,
    options: [
      { value: "cgst_sgst", label: "CGST + SGST" },
      { value: "igst", label: "IGST" },
    ],
  },
  {
    name: "placeOfSupplyStateCode",
    label: "Buyer state",
    type: "text",
  },
];
